<template>
  <div>
    <spinner v-if="loading"></spinner>

    <div v-else>
      <div>
        <h1 class="text-2xl font-bold text-center text-primary">
          ผลการประเมินด้านความเสี่ยงต่อสุขภาพ
        </h1>
        <h1 class="text-xl font-light text-center">Health Risk Assessment</h1>
      </div>

      <div class="">
        <p>แบ่งตามหน่วยงาน</p>
        <a-select
          @change="fetchFacultySummary"
          v-model="selectedFaculty"
          show-search
          placeholder="Select a person"
          option-filter-prop="children"
          style="width: 350px"
          class="mt-2"
        >
          <a-select-option v-for="faculty in faculties" :key="faculty" :value="faculty">
            {{ faculty }}
          </a-select-option>
        </a-select>
      </div>

      <div class="mt-8 card">
        <div class="mb-8">
          <h1 class="text-lg font-bold text-center">
            กราฟวงกลมแสดงสัดส่วนปัจจัยเสี่ยงในแต่ละด้าน
          </h1>
          <p class="text-xs text-center">
            จากบุคลากร {{ facultySummary.numberOfPeople }} คน
          </p>
        </div>

        <risk-count-pie-chart
          class="w-full text-left"
          :data="topicRatio"
          :labels="topicLabels"
        />
      </div>

      <div class="card">
        <h1 class="text-lg font-bold text-center">
          ตารางแสดงสัดส่วนปัจจัยเสี่ยงในแต่ละด้าน
        </h1>
        <p class="text-xs text-center">
          จากบุคลากร {{ facultySummary.numberOfPeople }} คน
        </p>

        <table class="min-w-full mt-16">
          <thead>
            <tr>
              <th
                class="px-6 py-3 text-sm font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50"
              >
                ปัจจัยเสี่ยง
              </th>
              <th
                class="px-6 py-3 text-sm font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50"
              >
                อัตราส่วนบุคลากรที่มีความเสี่ยง
              </th>
              <th
                class="px-6 py-3 text-sm font-medium leading-4 tracking-wider text-left text-gray-500 uppercase border-b border-gray-200 bg-gray-50"
              >
                คิดเป็นจำนวน
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="topic in facultySummary.riskTopics"
              :key="topic.name"
              class="bg-white"
            >
              <td
                class="px-6 py-4 whitespace``-no-wrap text-sm leading-5 font-medium text-gray-900"
              >
                {{ topic.name }}
              </td>
              <td class="px-6 py-4 text-sm leading-5 text-gray-500 whitespace-no-wrap">
                {{ topic.percent.toFixed(2) }} %
              </td>
              <td class="px-6 py-4 text-sm leading-5 text-gray-500 whitespace-no-wrap">
                {{ topic.count }} คน
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="card">
        <h1 class="text-lg font-bold text-center">แบ่งตามประเภทพนักงาน</h1>
        <p class="mb-2 text-xs text-center">
          จากบุคลากร {{ facultySummary.numberOfPeople }} คน
        </p>
        <risk-stacked-bar-chart
          class="bar-chart"
          :labels="facultySummary.groups.employeeType.groups"
          :low="facultySummary.groups.employeeType.finalResults.low.numbersOfPeople"
          :mid="facultySummary.groups.employeeType.finalResults.mid.numbersOfPeople"
          :high="facultySummary.groups.employeeType.finalResults.high.numbersOfPeople"
        >
        </risk-stacked-bar-chart>
      </div>

      <div class="mt-12 card">
        <h1 class="text-lg font-bold text-center">แบ่งตามเพศ</h1>
        <p class="mb-2 text-xs text-center">
          จากบุคลากร {{ facultySummary.numberOfPeople }} คน
        </p>
        <risk-stacked-bar-chart
          class="bar-chart"
          :labels="facultySummary.groups.gender.groups"
          :low="facultySummary.groups.gender.finalResults.low.numbersOfPeople"
          :mid="facultySummary.groups.gender.finalResults.mid.numbersOfPeople"
          :high="facultySummary.groups.gender.finalResults.high.numbersOfPeople"
        >
        </risk-stacked-bar-chart>
      </div>
    </div>
  </div>
</template>

<script>
import WhoqolStackedBarChart from "@/components/charts/WhoqolStackedBarChart";
import axios from "axios";
import { faculties } from "@/data/group.js";
import Spinner from "@/components/loading/Spinner";
import RiskStackedBarChart from "./charts/RiskStackedBarChart";
import RiskCountPieChart from "@/components/charts/RiskCountPieChart";
import EventBus from "@/events/event-bus.js";

export default {
  components: {
    RiskStackedBarChart,
    RiskCountPieChart,
    Spinner,
  },
  created() {
    this.fetchFacultySummary();

    EventBus.$on("dashboardYearChanged", this.fetchFacultySummary);
    EventBus.$on("dashboardEmployeeTypeChanged", this.fetchFacultySummary);
  },
  beforeDestroy() {
    EventBus.$off("dashboardYearChanged", this.fetchFacultySummary);
    EventBus.$off("dashboardEmployeeTypeChanged", this.fetchFacultySummary);
  },
  data() {
    return {
      faculties,
      facultySummary: {},
      selectedFaculty: "มหาวิทยาลัยเชียงใหม่",
      loading: false,
    };
  },
  methods: {
    async fetchFacultySummary() {
      this.loading = true;

      try {
        const response = await axios({
          method: "GET",
          url: `${process.env.VUE_APP_API_URL}/dashboard/risk/faculty?faculty=${this.selectedFaculty}&year=${this.$store.state.dashboardYear}&employeeType=${this.$store.state.dashboardEmployeeType}`,
          // headers: { authorization: `Bearer ${localStorage.getItem("TOKEN")}` }
        });

        this.facultySummary = response.data;

        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
  },
  computed: {
    topicLabels() {
      if (!this.facultySummary) {
        return [];
      }

      return this.facultySummary.riskTopics.map((topic) => topic.name);
    },
    topicRatio() {
      if (!this.facultySummary) {
        return [];
      }

      return this.facultySummary.riskTopics.map((topic) => topic.count);
    },
  },
};
</script>
